import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService } from '../services/api';
import ClipboardItem from './ClipboardItem';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Loader2, 
  FileText, 
  LogOut, 
  Settings,
  Clock,
  Calendar,
  TrendingUp,
  Users,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MapPin,
  Star,
  Zap,
  Play,
  Pause,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';

const PublicPage4 = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    content_type: '',
    sort_by: 'latest'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });
  const [autoPlay, setAutoPlay] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const fetchPublicItems = async (page = 1, search = searchTerm, filterParams = filters) => {
    try {
      const params = {
        page,
        limit: pagination.items_per_page,
        content_type: filterParams.content_type,
      };
      
      if (search) {
        params.search = search;
      }

      const response = await clipboardService.getPublicItems(params);
      setItems(response.items || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching public items:', error);
      toast.error('Failed to load public items');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPublicItems();
  }, []);

  // Auto-play effect for story mode
  useEffect(() => {
    if (autoPlay && items.length > 0) {
      const interval = setInterval(() => {
        setCurrentStoryIndex((prev) => (prev + 1) % items.length);
      }, 5000); // Change story every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoPlay, items.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPublicItems(1, searchTerm, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchPublicItems(1, searchTerm, newFilters);
  };

  const handlePageChange = (page) => {
    fetchPublicItems(page, searchTerm, filters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPublicItems(pagination.current_page, searchTerm, filters);
  };

  const handleUpdateItem = (itemId, updates) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 1) return 'Yesterday';
    return `${diffInDays} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading stories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Big Clipboard</h1>
                  <p className="text-xs text-gray-500">Story Hub</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                My Clipboard
              </Link>
              {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                <Link
                  to="/admin"
                  className="text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <Settings className="w-4 h-4 inline mr-1" />
                  Admin
                </Link>
              )}
              <button
                onClick={() => {/* Handle logout */}}
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <LogOut className="w-4 h-4 inline mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Story Mode Toggle */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">Story Mode</h2>
              <button
                onClick={() => setAutoPlay(!autoPlay)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  autoPlay 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {autoPlay ? (
                  <>
                    <Pause className="w-4 h-4" />
                    <span>Pause</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    <span>Auto Play</span>
                  </>
                )}
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {items.length > 0 && (
                  <span>Story {currentStoryIndex + 1} of {items.length}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search stories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </form>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filters.content_type}
                onChange={(e) => handleFilterChange('content_type', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="text">Text</option>
                <option value="link">Link</option>
                <option value="image">Image</option>
                <option value="long_text">Long Text</option>
                <option value="html">HTML</option>
              </select>
              
              <select
                value={filters.sort_by}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="latest">Latest</option>
                <option value="trending">Trending</option>
                <option value="popular">Most Popular</option>
              </select>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {refreshing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Timeline Content */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
          
          <div className="space-y-8">
            {items.map((item, index) => (
              <div key={item.id} className="relative">
                {/* Timeline Dot */}
                <div className="absolute left-6 top-6 w-4 h-4 bg-emerald-600 rounded-full border-4 border-white shadow-lg"></div>
                
                {/* Content Card */}
                <div className={`ml-16 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${
                  autoPlay && index === currentStoryIndex ? 'ring-2 ring-emerald-500 scale-105' : 'hover:shadow-md'
                }`}>
                  {/* Story Header */}
                  <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {item.author_name ? item.author_name.charAt(0).toUpperCase() : 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {item.author_name || 'Anonymous User'}
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span>{formatTimeAgo(item.created_at)}</span>
                            <span>•</span>
                            <span className="capitalize">{item.content_type || 'text'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                          <Heart className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-blue-500 transition-colors">
                          <MessageCircle className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-green-500 transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-yellow-500 transition-colors">
                          <Bookmark className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Story Content */}
                  <div className="p-6">
                    <ClipboardItem 
                      item={item} 
                      onUpdate={handleUpdateItem}
                      isPublicPage={true}
                    />
                  </div>
                  
                  {/* Story Footer */}
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{Math.floor(Math.random() * 100) + 10} views</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-4 h-4" />
                          <span>{Math.floor(Math.random() * 50) + 5} likes</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MessageCircle className="w-4 h-4" />
                          <span>{Math.floor(Math.random() * 20) + 2} comments</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                          Read Full Story
                        </button>
                        <Star className="w-4 h-4 text-yellow-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-12 flex justify-center">
            <nav className="flex space-x-2">
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    page === pagination.current_page
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPage4;